import { Button, Calendar, IconFont, Popover } from '@byecode/ui'
import type { ActorEvent, CustomViewConfig, RecordLikeProtocol, RichTextContentProtocol } from '@lighthouse/core'
import { BLOCK_SIZE } from '@lighthouse/core'
import type { ContainerLayoutContextValue, FlowLayoutNode } from '@lighthouse/shared'
import { ContainerLayoutProvider, useContainerLayoutContext, ViewEmptyState } from '@lighthouse/shared'
import type { BreakPointSize } from '@lighthouse/tools'
import { addDays, addMonths, addWeeks, isSameMonth, isSameWeek, lightFormat, startOfWeek } from 'date-fns'
import React, { forwardRef, memo, useMemo, useState } from 'react'
import styled from 'styled-components'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { CalendarView } from '../CustomViewWeekCalendarBlock/CalendarView'

const Root = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`

const WeekSwitcher = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 8px 0 12px;
`

const WeekRange = styled.div`
    min-width: 200px;
    padding: 0 8px;
    border-radius: 6px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    line-height: 28px;
    color: var(--color-black);
    cursor: pointer;
    user-select: none;

    &:hover {
        background-color: var(--color-gray-100);
    }
`

const PickerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    background-color: var(--color-gray-50);
    border-bottom: 1px solid var(--color-gray-200);
    font-size: 14px;
    line-height: 22px;
    color: var(--color-gray-900);
`

const WeekPickerPanel = styled.div`
    padding: 4px 8px 10px;

    table {
        border-spacing: 0 !important;
        margin: 0 !important;
    }

    td {
        padding: 4px 0 !important;
    }

    tr:has([data-week-selected]) td:first-child::before,
    tr:has([data-week-hovered]) td:first-child::before {
        content: '';
        position: absolute;
        left: 6px;
        right: calc(-600% + 6px);
        top: 3px;
        bottom: 3px;
        border-radius: 8px;
        pointer-events: none;
        z-index: 0;
    }

    tr:has([data-week-selected]) td:first-child::before {
        background-color: var(--color-gray-100);
    }

    tr:has([data-week-hovered]) td:first-child::before {
        background-color: var(--color-gray-200);
    }
`

const WeekCell = styled.div<{ selected?: boolean; hovered?: boolean; muted?: boolean }>`
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    color: ${({ selected, hovered, muted }) =>
        selected || hovered ? 'var(--color-gray-900)' : muted ? 'var(--color-gray-300)' : 'var(--color-gray-900)'};
`

interface CustomViewWeekCalendarBlockProps {
    firstDayOfWeek: Date
    node: FlowLayoutNode
    blockWidth: number
    breakPoint?: BreakPointSize
    readonly?: boolean
    onRenderRichText: (v: RichTextContentProtocol) => string
    onWeekChange?: (date: Date) => void
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
}

function WeekSwitcherBar({ weekStart, onWeekChange }: { weekStart: Date; onWeekChange: (date: Date) => void }) {
    const [opened, setOpened] = useState(false)
    const [hoverDate, setHoverDate] = useState<Date>()
    const weekStartsOn = weekStart.getDay() as 0 | 1 | 2 | 3 | 4 | 5 | 6
    const weekRangeText = `${lightFormat(weekStart, 'yyyy-MM-dd')} - ${lightFormat(addDays(weekStart, 6), 'yyyy-MM-dd')}`

    return (
        <WeekSwitcher>
            <Button
                size="xs"
                type="text"
                icon={<IconFont type="ArrowLeftSmall" />}
                onClick={() => onWeekChange(addWeeks(weekStart, -1))}
            />
            <Popover
                width={280}
                withinPortal
                position="bottom"
                opened={opened}
                onChange={next => {
                    setOpened(next)
                    if (!next) {
                        setHoverDate(undefined)
                    }
                }}
            >
                <Popover.Target>
                    <WeekRange>{weekRangeText}</WeekRange>
                </Popover.Target>
                <Popover.Dropdown compact>
                    <WeekPickerPanel>
                    <Calendar
                        date={weekStart}
                        showTime={false}
                        hideFooter
                        hideNow
                        firstDayOfWeek={weekStartsOn}
                        HeaderComponent={({ panelViewDate, onPanelViewDate }) => (
                            <PickerHeader>
                                <div>
                                    <Button
                                        size="sm"
                                        type="text"
                                        icon={<IconFont type="ArrowLeftDouble" />}
                                        onClick={() => onPanelViewDate?.(addMonths(panelViewDate, -12))}
                                    />
                                    <Button
                                        size="sm"
                                        type="text"
                                        icon={<IconFont type="ArrowLeftSmall" />}
                                        onClick={() => onPanelViewDate?.(addMonths(panelViewDate, -1))}
                                    />
                                </div>
                                <span>{lightFormat(panelViewDate, 'yyyy-MM')}</span>
                                <div>
                                    <Button
                                        size="sm"
                                        type="text"
                                        icon={<IconFont type="ArrowRightSmall" />}
                                        onClick={() => onPanelViewDate?.(addMonths(panelViewDate, 1))}
                                    />
                                    <Button
                                        size="sm"
                                        type="text"
                                        icon={<IconFont type="ArrowRightDouble" />}
                                        onClick={() => onPanelViewDate?.(addMonths(panelViewDate, 12))}
                                    />
                                </div>
                            </PickerHeader>
                        )}
                        onDateChange={date => {
                            if (!date) {
                                return
                            }
                            onWeekChange(startOfWeek(date, { weekStartsOn }))
                            setOpened(false)
                            setHoverDate(undefined)
                        }}
                        onCellMouseEnter={date => setHoverDate(date)}
                        onCellMouseLeave={() => setHoverDate(undefined)}
                        CellComponent={({ date: day, panelViewDate }) => {
                            if (!day) {
                                return null
                            }
                            const selected = isSameWeek(day, weekStart, { weekStartsOn })
                            const hovered = !!hoverDate && isSameWeek(day, hoverDate, { weekStartsOn })
                            return (
                                <WeekCell
                                    data-week-selected={selected || undefined}
                                    data-week-hovered={hovered || undefined}
                                    selected={selected}
                                    hovered={hovered}
                                    muted={!isSameMonth(day, panelViewDate)}
                                >
                                    {lightFormat(day, 'dd')}
                                </WeekCell>
                            )
                        }}
                    />
                    </WeekPickerPanel>
                </Popover.Dropdown>
            </Popover>
            <Button
                size="xs"
                type="text"
                icon={<IconFont type="ArrowRightSmall" />}
                onClick={() => onWeekChange(addWeeks(weekStart, 1))}
            />
        </WeekSwitcher>
    )
}

export const CustomViewWeekCalendarBlock = memo(
    forwardRef<HTMLDivElement, CustomViewWeekCalendarBlockProps>((props, ref) => {
        const { firstDayOfWeek, node, blockWidth, readonly, onRenderRichText, onWeekChange, onRecordClick } = props
        const { blockData, dataSource } = useViewBlockContext()
        const { id, title, config } = blockData
        const { breakPoint: baseBreakPointConfig, calendarStartDateField, anchor } = config as CustomViewConfig
        const { anchorId } = anchor || {}
        const effectiveAnchorId = useEffectiveAnchorId(anchorId)
        useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
        const { viewLayout, viewStyles, layout, emptyView } = baseBreakPointConfig || {}
        const { size = BLOCK_SIZE.md, gap, adaptive } = viewLayout || {}
        const showWeekSwitcher = viewStyles?.showWeekSwitcher ?? false

        const showEmpty = !dataSource

        const itemWidth = useMemo(() => {
            if (adaptive) return undefined
            const SIZE_MAP = {
                [BLOCK_SIZE.sm]: 90,
                [BLOCK_SIZE.md]: 110,
                [BLOCK_SIZE.lg]: 130,
                [BLOCK_SIZE.xl]: 150
            }
            return SIZE_MAP[size]
        }, [adaptive, size])

        const { level } = useContainerLayoutContext()

        const contextValue: ContainerLayoutContextValue = useMemo(() => {
            return {
                parentId: id,
                level: level + 1,
                size: {
                    width: {
                        size: blockWidth,
                        unit: 'px'
                    },
                    height: {
                        size: 'auto'
                    }
                },
                layout: {
                    ...layout
                }
            }
        }, [id, level, blockWidth, layout])

        if (showEmpty) {
            return <ViewEmptyState id={effectiveAnchorId} config={emptyView} onRenderRichText={onRenderRichText} />
        }

        return (
            <ContainerLayoutProvider value={contextValue}>
                <Root ref={ref}>
                    {showWeekSwitcher && onWeekChange ? (
                        <WeekSwitcherBar weekStart={firstDayOfWeek} onWeekChange={onWeekChange} />
                    ) : null}
                    <CalendarView
                        id={effectiveAnchorId}
                        firstDayOfWeek={firstDayOfWeek}
                        node={node}
                        title={title}
                        gap={gap?.size || 0}
                        readonly={readonly}
                        itemWidth={itemWidth}
                        calendarStartDateField={calendarStartDateField}
                        viewStyles={viewStyles}
                        onRecordClick={onRecordClick}
                    />
                </Root>
            </ContainerLayoutProvider>
        )
    })
)
